import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { motion, HTMLMotionProps } from "framer-motion";
import { cn } from "@/lib/utils";
const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-modern text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-inherit [&_svg]:transition-colors [&_svg]:duration-200 touch-target mobile-text-optimize relative overflow-hidden group", {
  variants: {
    variant: {
      default: "bg-primary text-primary-foreground shadow-button hover:shadow-button-hover hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 hover:scale-[1.02]",
      destructive: "bg-destructive text-destructive-foreground shadow-button hover:shadow-button-hover hover:-translate-y-0.5 active:translate-y-0 hover:scale-[1.02]",
      outline: "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground hover:shadow-button hover:-translate-y-0.5 active:translate-y-0 hover:scale-[1.02] backdrop-blur-sm",
      secondary: "bg-secondary text-secondary-foreground shadow-button hover:shadow-button-hover hover:-translate-y-0.5 active:translate-y-0 hover:scale-[1.02]",
      ghost: "hover:bg-accent hover:text-accent-foreground rounded-lg hover:scale-105 transition-all duration-200",
      link: "text-primary underline-offset-4 hover:underline hover:scale-105 transition-transform duration-200",
      gradient: "bg-gradient-modern text-white shadow-button hover:shadow-button-hover hover:-translate-y-0.5 active:translate-y-0 before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700 hover:scale-[1.02]",
      'luxury-gradient': "bg-gradient-to-br from-luxury-gold to-luxury-gold-dark text-primary-foreground shadow-luxury hover:shadow-luxury-hover hover:-translate-y-0.5 transition-all duration-300",
      glass: "bg-white/10 backdrop-blur-glass border border-white/20 text-foreground shadow-glass hover:bg-white/20 hover:shadow-glass-dark hover:-translate-y-0.5 active:translate-y-0 hover:scale-[1.02]",
      floating: "bg-primary text-primary-foreground rounded-full shadow-modern hover:shadow-modern-lg hover:scale-110 active:scale-95 transition-all duration-200",
      modern: "bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-primary-foreground shadow-modern hover:shadow-modern-lg hover:-translate-y-1 active:translate-y-0 hover:scale-[1.02] border border-primary/20",
      neon: "bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] hover:scale-105 transition-all duration-300"
    },
    size: {
      default: "h-10 px-4 py-2 min-h-touch",
      sm: "h-9 rounded-modern px-3 min-h-[36px] text-sm",
      lg: "h-12 rounded-modern px-8 min-h-touch-lg text-base font-semibold",
      icon: "h-10 w-10 min-h-touch min-w-touch rounded-modern",
      pill: "h-10 px-6 rounded-pill min-h-touch",
      floating: "h-14 w-14 rounded-full shadow-modern",
      micro: "h-6 px-2 text-xs rounded-pill min-h-[24px]",
      xl: "h-14 px-10 text-lg font-bold rounded-modern min-h-[56px]"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  animated?: boolean;
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  animated = false,
  children,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : "button";
  const buttonContent = <>
        {loading && <div className="absolute inset-0 flex items-center justify-center bg-current/10 backdrop-blur-sm rounded-inherit">
            <motion.div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full" animate={{
        rotate: 360
      }} transition={{
        duration: 1,
        repeat: Infinity,
        ease: "linear"
      }} />
          </div>}
        <motion.span className={cn("transition-opacity duration-200 flex items-center gap-2", loading && "opacity-0")} initial={animated ? {
      opacity: 0,
      y: 10
    } : undefined} animate={animated ? {
      opacity: 1,
      y: 0
    } : undefined} transition={animated ? {
      duration: 0.3
    } : undefined}>
          {children}
        </motion.span>
      </>;

  // When using asChild (Slot), we need to ensure we pass exactly one child element
  if (asChild) {
    return <Slot className={cn(buttonVariants({
      variant,
      size,
      className
    }))} ref={ref} {...props}>
          <span className="relative">
            {buttonContent}
          </span>
        </Slot>;
  }
  if (animated) {
    // Extract HTML event handlers that conflict with Framer Motion
    const {
      onDrag,
      onDragStart,
      onDragEnd,
      onAnimationStart,
      onAnimationEnd,
      onAnimationIteration,
      onTransitionEnd,
      ...buttonProps
    } = props;
    return <motion.button className={cn(buttonVariants({
      variant,
      size,
      className
    }))} ref={ref} disabled={loading || props.disabled} whileHover={{
      scale: variant === "ghost" || variant === "link" ? 1.05 : 1.02
    }} whileTap={{
      scale: 0.98
    }} transition={{
      type: "spring",
      stiffness: 300,
      damping: 20
    }} {...buttonProps}>
          {buttonContent}
        </motion.button>;
  }
  return <button className={cn(buttonVariants({
    variant,
    size,
    className
  }))} ref={ref} disabled={loading || props.disabled} {...props}>
        {buttonContent}
      </button>;
});
Button.displayName = "Button";
export { Button, buttonVariants };